import React, { memo } from 'react';
import { useSelector } from 'react-redux';

import { Box } from '@mui/material';
import StatusIconCDS from '@sharedCR/coreDesignSystem/StatusIconCDS';
import PropTypes from 'prop-types';

import { AiFormStatus } from 'constants/aiProcessRuns';
import { StatusEnum } from 'constants/status';

import { selectFormByExecution } from 'store/aiProcessRuns';

import { resolveWorstFieldTriageTone } from '../../../ExecutionDetailDrawer/StepDetailPane/FormReviewCard/formReview.utils';
import {
  FORM_REVIEW_LABELS,
  FormReviewTestIds,
  TRIAGE_TONE_TO_STATUS,
} from '../../../ExecutionDetailDrawer/StepDetailPane/FormReviewCard/FormReviewCard.constants';

import { triageDotStyles } from './RunFormTriageDot.styles';

// A red (flagged) field is the most urgent signal and wins; otherwise a form
// awaiting review surfaces as a warning even when every field reads green, so
// the run row flags it as actionable. Falls back to the field tone.
const resolveDotStatus = (toneStatus, isNeedsReview) => {
  if (toneStatus === StatusEnum.ERROR) {
    return StatusEnum.ERROR;
  }

  if (isNeedsReview) {
    return StatusEnum.WARNING;
  }

  return toneStatus ?? StatusEnum.WARNING;
};

const RunFormTriageDot = ({ executionId }) => {
  const form = useSelector(selectFormByExecution(executionId));

  if (!form) {
    return null;
  }

  const worstTone = resolveWorstFieldTriageTone(form);
  const toneStatus = worstTone ? TRIAGE_TONE_TO_STATUS[worstTone] : null;
  const isNeedsReview = form.status === AiFormStatus.READY_FOR_REVIEW;

  if (!toneStatus && !isNeedsReview) {
    return null;
  }

  const status = resolveDotStatus(toneStatus, isNeedsReview);
  const title = isNeedsReview ? FORM_REVIEW_LABELS.NEEDS_REVIEW_CHIP : null;

  return (
    <Box data-testid={FormReviewTestIds.TRIAGE_DOT} sx={triageDotStyles} title={title}>
      <StatusIconCDS size={14} type={status} />
    </Box>
  );
};

RunFormTriageDot.propTypes = {
  executionId: PropTypes.string.isRequired,
};

export default memo(RunFormTriageDot);
